import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllWorkItems, fetchMyWorkItems } from "../../Redux/Thunks/projectManagementThunk";
import {
  WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY,
  readStoredListPageSize,
} from "../../Utility/Pagination/listPaginationConstants";
import {
  fetchSavedSearches,
  createSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
} from "../../Redux/Thunks/savedSearchesThunk";
import {
  selectSavedSearchList,
  selectSavedSearchesSaving,
} from "../../Redux/Slices/savedSearchesSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { selectAuthUserId } from "../../Redux/Slices/authSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { selectSystemListItems } from "../../Redux/Slices/systemListsSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { fetchKanbanBoardBootstrap } from "../../Redux/Thunks/kanbanColumnsThunk";
import { KANBAN_DEFAULT_LIMIT } from "../../constants";
import { GLOBAL_BOARD_KEY } from "./IssueKnban";
import { usePermissions } from "../../hooks/usePermissions";
import IssuesNavigation from "./IssuesNavigation";
import {
  ISSUES_SECTION_NAV_ITEMS,
  issuesUrlActiveTab,
  canUseIssuesTab,
  firstAllowedIssuesTabId,
  buildIssuesPathForTab,
} from "./issuesTabPermissions";
import AllIssues from "./AllIssues";
import MyIssue from "./MyIssue";
import IssueKanban from "./IssueKnban";
import WorkItemDetailModal from "./WorkItemDetailModal";
import {
  navigateWorkItemDetailModal,
  getWorkItemDetailModalStateAfterSave,
} from "../../utils/workItemDetailModalNavigation";
import SavedSearchBar from "./SavedSearchBar";
import FilterChips from "./FilterChips";
import { normalizeSavedSearchListingFilters } from "../Projects/project-detail/issues/issues.mapper";
import {
  getListFromApiResponse,
  mapUserToAssigneeOptionForDropdown,
  mapSystemListItemToOption,
  mapTeamsToFilterOptions,
  ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS,
} from "./issueMappers";
import { mapProjectsToFilterOptions } from "../Projects/projectMappers";
// ------------------------------------------------------------------
// Filter defaults — all-issue and kanban tabs share this state (showEpics is per-tab, not here)
// ------------------------------------------------------------------
const ALL_ISSUES_FILTER_DEFAULT = {
  search: "",
  type: [],
  status: [],
  priority: [],
  assignee: [],
  reporter: [],
  team: [],
  client: [],
  project: [],
  dueAfter: "",
  dueBefore: "",
};

function parseKanbanQuickFilterAssignees(searchParams) {
  const assigneeParam = searchParams.get("assignee");
  if (assigneeParam) {
    return assigneeParam.split(",").filter(Boolean).map(String);
  }
  /** Legacy URL param — mapped to assignee. */
  const legacy = searchParams.get("participantUserIds");
  if (legacy) {
    return legacy.split(",").filter(Boolean).map(String);
  }
  return [];
}

function resolveInitialKanbanAssignees(isKanbanTab, searchParams, loggedInUserId) {
  if (!isKanbanTab) return [];
  const fromUrl = parseKanbanQuickFilterAssignees(searchParams);
  if (fromUrl.length > 0) return fromUrl;
  return loggedInUserId ? [loggedInUserId] : [];
}

function parseShowEpicsFromParams(searchParams) {
  return searchParams.get("showEpics") === "true";
}

const MULTI_KEYS = new Set([
  "client",
  "project",
  "type",
  "status",
  "priority",
  "assignee",
  "reporter",
  "team",
]);

// ------------------------------------------------------------------
// URL ↔ filter serialisation helpers
// ------------------------------------------------------------------
function encodeFiltersToParams(filter, savedSearchId, showEpics = false) {
  const params = {};
  Object.entries(filter).forEach(([k, v]) => {
    if (MULTI_KEYS.has(k)) {
      if (Array.isArray(v) && v.length > 0) params[k] = v.join(",");
    } else {
      const s = String(v ?? "").trim();
      if (s) params[k] = s;
    }
  });
  if (showEpics === true) params.showEpics = "true";
  if (savedSearchId) params.savedSearch = savedSearchId;
  return params;
}

function parseFiltersFromParams(searchParams) {
  const filter = { ...ALL_ISSUES_FILTER_DEFAULT };
  MULTI_KEYS.forEach((k) => {
    const v = searchParams.get(k);
    filter[k] = v ? v.split(",").filter(Boolean) : [];
  });
  filter.search = searchParams.get("search") ?? "";
  filter.dueAfter = searchParams.get("dueAfter") ?? "";
  filter.dueBefore = searchParams.get("dueBefore") ?? "";
  return {
    filter,
    savedSearch: searchParams.get("savedSearch") ?? null,
  };
}

const Issues = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();

  const visibleIssuesNavItems = useMemo(
    () => ISSUES_SECTION_NAV_ITEMS.filter((item) => canUseIssuesTab(can, item.id)),
    [can]
  );

  useLayoutEffect(() => {
    const first = firstAllowedIssuesTabId(can);
    if (!first) return;

    const rawTab = searchParams.get("tab");
    const hasExplicitTab = rawTab === "my-issue" || rawTab === "all-issue";
    const urlTab = issuesUrlActiveTab(searchParams);

    if (!canUseIssuesTab(can, urlTab)) {
      navigate(buildIssuesPathForTab(first), { replace: true });
      return;
    }

    // Sidebar "Work Items" → `/issues` with no `tab`: use first permitted tab (canonical URL).
    if (!hasExplicitTab && first !== "kanban") {
      navigate(buildIssuesPathForTab(first), { replace: true });
    }
  }, [searchParams, can, navigate]);

  const savedSearchList = useSelector(selectSavedSearchList);
  const savedSearchesSaving = useSelector(selectSavedSearchesSaving);
  const loggedInUserId = useSelector(selectAuthUserId);
  const authProfileResolved = useSelector((state) => state.auth?.profileResolved ?? true);

  // Reference data for FilterChips label resolution
  const usersData = useSelector(selectUsersData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const clientsData = useSelector(selectClientsData);
  const teamsData = useSelector(selectTeamsData);
  const typeItems = useSelector((s) => selectSystemListItems(s, "WORK_ITEM_TYPE"));
  const priorityItems = useSelector((s) => selectSystemListItems(s, "PRIORITY"));

  const [activeTab, setActiveTab] = useState(() => issuesUrlActiveTab(searchParams));

  // ── Shared filter state — used by both all-issue and kanban tabs ──
  const [sharedFilter, setSharedFilter] = useState(() => {
    const { filter } = parseFiltersFromParams(searchParams);
    return filter;
  });
  /** All work items — independent from My work items and Kanban quick-filter resets. */
  const [allIssueShowEpics, setAllIssueShowEpics] = useState(() =>
    parseShowEpicsFromParams(searchParams)
  );
  /** My work items — never synced with All tab or URL. */
  const [myIssueShowEpics, setMyIssueShowEpics] = useState(false);
  /** Global Kanban — On Hold column at end when true. */
  const [kanbanShowOnHold, setKanbanShowOnHold] = useState(false);
  /** Global Kanban — own search box (server-side `search`, independent of the All Issues filter). */
  const [kanbanBoardSearchTerm, setKanbanBoardSearchTerm] = useState("");
  const [activeSavedSearchId, setActiveSavedSearchId] = useState(() => {
    const { savedSearch } = parseFiltersFromParams(searchParams);
    return savedSearch;
  });
  const [isDirty, setIsDirty] = useState(false);
  // Incremented when debounced filter fires → causes IssueList to sync appliedFilter
  const [filterSyncToken, setFilterSyncToken] = useState(0);

  // const [activeTab, setActiveTab] = useState(() => issuesUrlActiveTab(location.search));
  const isKanbanRoute = issuesUrlActiveTab(searchParams) === "kanban";
  /** Global Kanban quick filter — maps to API `assigneeId` on all Kanban GETs. */
  const [globalKanbanAssignees, setGlobalKanbanAssignees] = useState(() =>
    resolveInitialKanbanAssignees(isKanbanRoute, searchParams, loggedInUserId)
  );
  /** When true, user cleared quick filter — do not auto-default to logged-in user again. */
  const userClearedQuickFilterRef = useRef(false);
  const [globalWorkItemModal, setGlobalWorkItemModal] = useState(null);
  const globalListRefetchRef = useRef(null);

  // Default Kanban quick filter to logged-in user when Kanban tab is active (unless user cleared it).
  useEffect(() => {
    if (activeTab !== "kanban" || !loggedInUserId || userClearedQuickFilterRef.current) return;
    setGlobalKanbanAssignees((prev) => (prev.length > 0 ? prev : [loggedInUserId]));
  }, [activeTab, loggedInUserId]);

  const handleGlobalKanbanAssigneesChange = useCallback((next) => {
    setGlobalKanbanAssignees((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      const list = Array.isArray(resolved) ? resolved.map(String) : [];
      userClearedQuickFilterRef.current = list.length === 0;
      return list;
    });
  }, []);

  // ── Debounce sharedFilter — URL sync only (no auto-fetch) ──
  const debouncedFilter = useDebounce(sharedFilter, 400);
  /** Debounced Kanban board search box — drives the `search` query param on Kanban GETs. */
  const debouncedKanbanBoardSearchTerm = useDebounce(kanbanBoardSearchTerm, 400);

  /** Kanban quick filter on → strip saved-search id from URL/API semantics immediately (not debounced). */
  const kanbanQuickFilterActive = useMemo(() => {
    if (activeTab !== "kanban") return false;
    return globalKanbanAssignees.length > 0;
  }, [activeTab, globalKanbanAssignees]);

  const savedSearchIdForUrl = kanbanQuickFilterActive ? null : activeSavedSearchId;

  /** When quick filter is on, URL carries assignee + search (+ dates) only. */
  const filterForUrlEncode = useMemo(() => {
    if (!kanbanQuickFilterActive) return debouncedFilter;
    return {
      ...ALL_ISSUES_FILTER_DEFAULT,
      assignee: globalKanbanAssignees.map(String),
      search: sharedFilter.search ?? "",
      dueAfter: sharedFilter.dueAfter ?? "",
      dueBefore: sharedFilter.dueBefore ?? "",
    };
  }, [kanbanQuickFilterActive, debouncedFilter, globalKanbanAssignees, sharedFilter]);

  /** Kanban GET listing scope — quick filter sends assignee-only params via `assigneeId`. */
  const listingFilterForKanbanFetch = useMemo(() => {
    if (activeTab !== "kanban") return debouncedFilter;
    if (globalKanbanAssignees.length > 0) {
      return {
        ...ALL_ISSUES_FILTER_DEFAULT,
        assignee: globalKanbanAssignees.map(String),
        search: sharedFilter.search ?? "",
      };
    }
    return sharedFilter;
  }, [activeTab, debouncedFilter, sharedFilter, globalKanbanAssignees]);

  /** Full listing scope for global Kanban API (saved filter + quick filter assignee). */
  const kanbanListingForApi = useMemo(() => {
    if (activeTab !== "kanban") return null;
    return {
      ...listingFilterForKanbanFetch,
      // Kanban board's own search box is authoritative for Kanban GETs.
      search: debouncedKanbanBoardSearchTerm,
      showEpics: allIssueShowEpics,
      showOnHold: kanbanShowOnHold,
    };
  }, [
    activeTab,
    listingFilterForKanbanFetch,
    debouncedKanbanBoardSearchTerm,
    allIssueShowEpics,
    kanbanShowOnHold,
  ]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (activeTab !== "all-issue" && activeTab !== "kanban") return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        [...MULTI_KEYS, "search", "dueAfter", "dueBefore", "savedSearch", "showEpics"].forEach(
          (k) => next.delete(k)
        );
        // Never inherit `tab` from prev — stale merges kept tab=all-issue after navigating to Kanban.
        next.delete("tab");
        const encoded = encodeFiltersToParams(
          filterForUrlEncode,
          savedSearchIdForUrl,
          allIssueShowEpics
        );
        Object.entries(encoded).forEach(([k, v]) => next.set(k, v));
        if (activeTab === "all-issue") {
          next.set("tab", "all-issue");
        }
        return next;
      },
      { replace: true }
    );
  }, [activeTab, filterForUrlEncode, savedSearchIdForUrl, allIssueShowEpics, setSearchParams]);

  // Sync activeTab when URL changes (browser back/forward)
  useEffect(() => {
    setActiveTab(issuesUrlActiveTab(searchParams));
  }, [searchParams]);

  // Load saved searches on mount
  useEffect(() => {
    dispatch(fetchSavedSearches());
  }, [dispatch]);

  // Teams list for filter chip label resolution
  useEffect(() => {
    dispatch(fetchTeamsData({ getAll: true }));
  }, [dispatch]);

  // ── Filter handlers ──
  const handleSharedFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setSharedFilter((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setActiveSavedSearchId(null);
  }, []);

  const handleSharedFilterReset = useCallback(() => {
    setSharedFilter({ ...ALL_ISSUES_FILTER_DEFAULT });
    setAllIssueShowEpics(false);
    setIsDirty(false);
    setActiveSavedSearchId(null);
  }, []);

  const handleAllIssueShowEpicsChange = useCallback((checked) => {
    setAllIssueShowEpics(checked);
    setIsDirty(true);
    setActiveSavedSearchId(null);
  }, []);

  const handleMyIssueShowEpicsChange = useCallback((checked) => {
    setMyIssueShowEpics(checked);
  }, []);

  // ── Saved search handlers ──
  const handleApplySavedSearch = useCallback(
    (savedSearch) => {
      const normalized = normalizeSavedSearchListingFilters(savedSearch.filters ?? {});
      const { showEpics: savedShowEpics, ...normalizedWithoutShowEpics } = normalized;
      const base = savedSearch.filters
        ? { ...ALL_ISSUES_FILTER_DEFAULT, ...normalizedWithoutShowEpics }
        : { ...ALL_ISSUES_FILTER_DEFAULT };
      setAllIssueShowEpics(savedShowEpics === true || savedShowEpics === "true");
      setSharedFilter(base);
      setActiveSavedSearchId(savedSearch.id);
      setIsDirty(false);
      setFilterSyncToken((n) => n + 1);
      if (activeTab === "kanban") {
        /** Saved search replaces quick filter selection on Kanban. */
        userClearedQuickFilterRef.current = true;
        setGlobalKanbanAssignees([]);
        dispatch(
          fetchKanbanBoardBootstrap({
            boardKey: GLOBAL_BOARD_KEY,
            listingFilter: {
              ...base,
              showEpics: savedShowEpics === true || savedShowEpics === "true",
              showOnHold: kanbanShowOnHold,
            },
            limit: KANBAN_DEFAULT_LIMIT,
          })
        );
      }
    },
    [activeTab, dispatch, kanbanShowOnHold]
  );

  const filterChipOptionMaps = useMemo(() => {
    const toOpt = (item) => mapSystemListItemToOption(item);
    return {
      assignee: getListFromApiResponse(usersData).map(mapUserToAssigneeOptionForDropdown),
      reporter: getListFromApiResponse(usersData).map(mapUserToAssigneeOptionForDropdown),
      project: mapProjectsToFilterOptions(projectsData),
      client: getListFromApiResponse(clientsData).map((c) => ({
        label: c.name ?? c.clientName ?? "",
        value: String(c.id ?? c.clientId ?? ""),
      })),
      team: mapTeamsToFilterOptions(teamsData),
      type: Array.isArray(typeItems) ? typeItems.map(toOpt) : [],
      priority: Array.isArray(priorityItems) ? priorityItems.map(toOpt) : [],
      status: ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS,
    };
  }, [usersData, projectsData, clientsData, teamsData, typeItems, priorityItems]);

  const toSaveableFilters = useCallback(
    (f) => {
      const rest = { ...(f ?? {}) };
      delete rest.dueAfter;
      delete rest.dueBefore;
      delete rest.showEpics;
      return { ...rest, showEpics: allIssueShowEpics };
    },
    [allIssueShowEpics]
  );

  const handleSaveSearch = useCallback(async () => {
    if (!activeSavedSearchId) return;
    try {
      await dispatch(
        updateSavedSearch({ id: activeSavedSearchId, filters: toSaveableFilters(sharedFilter) })
      ).unwrap();
      setIsDirty(false);
    } catch {
      /* toast handled by thunk */
    }
  }, [dispatch, activeSavedSearchId, sharedFilter, toSaveableFilters]);

  const handleSaveAsSearch = useCallback(
    async (name) => {
      if (!name?.trim()) return;
      try {
        const result = await dispatch(
          createSavedSearch({ name: name.trim(), filters: toSaveableFilters(sharedFilter) })
        ).unwrap();
        if (result?.id) {
          setActiveSavedSearchId(result.id);
          setIsDirty(false);
        }
      } catch {
        /* toast handled by thunk */
      }
    },
    [dispatch, sharedFilter, toSaveableFilters]
  );

  const handleDeleteSavedSearch = useCallback(
    async (id) => {
      try {
        await dispatch(deleteSavedSearch(id)).unwrap();
        if (activeSavedSearchId === id) {
          setActiveSavedSearchId(null);
          setIsDirty(false);
        }
        dispatch(fetchSavedSearches());
      } catch {
        /* toast handled by thunk */
      }
    },
    [dispatch, activeSavedSearchId]
  );

  const registerGlobalListRefetch = useCallback((fn) => {
    globalListRefetchRef.current = fn;
  }, []);

  // ── Tab navigation ──
  const handleTabChange = useCallback(
    (tab) => {
      if (!canUseIssuesTab(can, tab)) return;
      setGlobalWorkItemModal(null);
      if (tab === "kanban") {
        // List / My tabs must not carry filters into Kanban — fetch unscoped board unless user picks Kanban quick filter / saved search here.
        if (activeTab === "all-issue" || activeTab === "my-issue") {
          setSharedFilter({ ...ALL_ISSUES_FILTER_DEFAULT });
          setActiveSavedSearchId(null);
          setIsDirty(false);
          setFilterSyncToken((n) => n + 1);
        }
        setActiveTab("kanban");
        navigate("/issues", { replace: true });
      } else if (tab === "my-issue") {
        setActiveTab("my-issue");
        navigate("/issues?tab=my-issue", { replace: true });
      } else {
        setActiveTab("all-issue");
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", "all-issue");
            return next;
          },
          { replace: false }
        );
      }
    },
    [navigate, setSearchParams, activeTab, can]
  );

  useEffect(() => {
    if (activeTab !== "kanban" || !kanbanListingForApi) return;
    /** Skip until default assignee is applied — avoids an unscoped bootstrap fetch. */
    if (
      globalKanbanAssignees.length === 0 &&
      !userClearedQuickFilterRef.current &&
      (loggedInUserId || !authProfileResolved)
    ) {
      return;
    }
    dispatch(
      fetchKanbanBoardBootstrap({
        boardKey: GLOBAL_BOARD_KEY,
        listingFilter: kanbanListingForApi,
        limit: KANBAN_DEFAULT_LIMIT,
      })
    );
  }, [
    dispatch,
    activeTab,
    kanbanListingForApi,
    globalKanbanAssignees.length,
    loggedInUserId,
    authProfileResolved,
  ]);

  // ── Work item detail modal ──
  const openGlobalWorkItemDetail = useCallback((issueId, projectId) => {
    if (!issueId || !projectId) return;
    setGlobalWorkItemModal({
      projectSlug: String(projectId),
      issueId: String(issueId),
      epicParentId: null,
    });
  }, []);

  const closeGlobalWorkItemDetail = useCallback(() => setGlobalWorkItemModal(null), []);

  const handleGlobalWorkItemSaved = useCallback(async () => {
    const nextModal = getWorkItemDetailModalStateAfterSave(globalWorkItemModal);
    try {
      if (activeTab === "kanban" && kanbanListingForApi) {
        await dispatch(
          fetchKanbanBoardBootstrap({
            boardKey: GLOBAL_BOARD_KEY,
            listingFilter: kanbanListingForApi,
            limit: KANBAN_DEFAULT_LIMIT,
          })
        ).unwrap();
      } else {
        const refetch = globalListRefetchRef.current;
        if (typeof refetch === "function") {
          await refetch();
        } else if (activeTab === "my-issue") {
          await dispatch(
            fetchMyWorkItems({
              page: 1,
              limit: readStoredListPageSize(WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY),
            })
          ).unwrap();
        } else if (activeTab === "all-issue") {
          await dispatch(
            fetchAllWorkItems({
              page: 1,
              limit: readStoredListPageSize(WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY),
            })
          ).unwrap();
        }
      }
    } catch {
      /* still navigate or close */
    }
    if (nextModal) {
      setGlobalWorkItemModal(nextModal);
    } else {
      closeGlobalWorkItemDetail();
    }
  }, [dispatch, activeTab, closeGlobalWorkItemDetail, kanbanListingForApi, globalWorkItemModal]);

  // Shared save props — Save As UI lives on SavedSearchBar for global Issues (not IssueList toolbar)
  const saveFilterProps = {
    isDirty,
    activeSavedSearchId,
    saving: savedSearchesSaving,
    onSave: handleSaveSearch,
    onSaveAs: handleSaveAsSearch,
    showSaveAs: false,
  };

  return (
    <div>
      <IssuesNavigation
        items={visibleIssuesNavItems}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Saved search dropdown — all-issue and kanban tabs */}
      {(activeTab === "all-issue" || activeTab === "kanban") && (
        <SavedSearchBar
          savedSearches={savedSearchList}
          activeSavedSearchId={activeSavedSearchId}
          onSelect={handleApplySavedSearch}
          onDelete={handleDeleteSavedSearch}
          isDirty={isDirty}
          onSaveAs={handleSaveAsSearch}
          saving={savedSearchesSaving}
        />
      )}

      {/* Filter chips — all-issue tab only */}
      {activeTab === "all-issue" && (
        <FilterChips
          filter={sharedFilter}
          optionMaps={filterChipOptionMaps}
          onRemove={(fieldName, value) => {
            setSharedFilter((prev) => {
              if (MULTI_KEYS.has(fieldName)) {
                return {
                  ...prev,
                  [fieldName]: Array.isArray(prev[fieldName])
                    ? prev[fieldName].filter((v) => v !== value)
                    : [],
                };
              }
              return { ...prev, [fieldName]: "" };
            });
            setIsDirty(true);
            setActiveSavedSearchId(null);
          }}
        />
      )}

      {/* Tab Content */}
      {activeTab === "all-issue" && (
        <AllIssues
          filter={sharedFilter}
          showEpics={allIssueShowEpics}
          onShowEpicsChange={handleAllIssueShowEpicsChange}
          onFilterChange={handleSharedFilterChange}
          onFilterReset={handleSharedFilterReset}
          syncToken={filterSyncToken}
          onOpenWorkItemDetail={openGlobalWorkItemDetail}
          registerGlobalListRefetch={registerGlobalListRefetch}
          saveFilterProps={saveFilterProps}
        />
      )}

      {activeTab === "my-issue" && (
        <MyIssue
          showEpics={myIssueShowEpics}
          onShowEpicsChange={handleMyIssueShowEpicsChange}
          onOpenWorkItemDetail={openGlobalWorkItemDetail}
          registerGlobalListRefetch={registerGlobalListRefetch}
        />
      )}

      {activeTab === "kanban" && (
        <IssueKanban
          onOpenWorkItemDetail={openGlobalWorkItemDetail}
          assigneeFilterIds={globalKanbanAssignees}
          onAssigneeFilterIdsChange={handleGlobalKanbanAssigneesChange}
          kanbanListingFilter={kanbanListingForApi}
          showOnHold={kanbanShowOnHold}
          onShowOnHoldChange={setKanbanShowOnHold}
          searchTerm={kanbanBoardSearchTerm}
          onSearchTermChange={setKanbanBoardSearchTerm}
        />
      )}

      {globalWorkItemModal ? (
        <WorkItemDetailModal
          projectSlug={globalWorkItemModal.projectSlug}
          issueId={globalWorkItemModal.issueId}
          onClose={closeGlobalWorkItemDetail}
          onSaved={handleGlobalWorkItemSaved}
          onOpenRelatedWorkItem={(relatedId, options) => {
            setGlobalWorkItemModal((prev) => navigateWorkItemDetailModal(prev, relatedId, options));
          }}
          onProjectIdChange={(newProjectId) => {
            setGlobalWorkItemModal((prev) => {
              if (!newProjectId || !prev?.issueId) return prev;
              return {
                projectSlug: String(newProjectId),
                issueId: prev.issueId,
                epicParentId: null,
              };
            });
          }}
        />
      ) : null}
    </div>
  );
};

export default Issues;
